import React, { useState, useEffect, useMemo } from "react";
import { Box, Typography, Card, CardContent } from "@mui/material";
import CircleIcon from "@mui/icons-material/Circle";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import EventNoteIcon from "@mui/icons-material/EventNote";
import ChatBubbleIcon from "@mui/icons-material/ChatBubble";
import EmailIcon from "@mui/icons-material/Email";
import ReportIcon from "@mui/icons-material/Report";
import axios from "axios";
import moment from "moment";
import { motion } from "framer-motion"; // For animations
import api, { API_BASE_URL } from "../../src/utils/api";


const ViolationsTimeline = ({ violation_id }) => {
  const [timelineData, setTimelineData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchTimeline = async () => {
      try {
        const response = await api.get(
          `/violationsTimeline/${violation_id}`
        );
        console.log("Timeline Data:", response.data); // Debugging
        setTimelineData(response.data);
      } catch (error) {
        console.error("Error fetching timeline data:", error);
        setError("Failed to load timeline data.");
      } finally {
        setLoading(false);
      }
    };

    fetchTimeline();
  }, [violation_id]);

  const combinedTimeline = useMemo(() => {
    if (!timelineData || !timelineData.violation) return [];

    const { violation, comments = [], events = [], emails = [] } = timelineData;

    const timeline = [
      { type: "violation", title: "Violation Created", time: violation.created_at },
      ...comments.map(({ comment_text, sent_at }) => ({
        type: "comment",
        title: comment_text,
        time: sent_at || new Date().toISOString(),
      })),
      ...events.map(({ title, created_at }) => ({
        type: "event",
        title,
        time: created_at || new Date().toISOString(),
      })),
      ...emails.map(({ subject, sent_at }) => ({
        type: "email",
        title: subject,
        time: sent_at || new Date().toISOString(),
      })),
      ...(violation.updated_at
        ? [{ type: "violation", title: "Violation Updated", time: violation.updated_at }]
        : []),
    ];

    return timeline.sort((a, b) => new Date(a.time) - new Date(b.time));
  }, [timelineData]);

  if (loading) return <Typography>Loading timeline...</Typography>;
  if (error) return <Typography color="error">{error}</Typography>;

  return (
    <Box sx={{ padding: 2 }}>
      <Typography variant="h6" sx={{ mb: 3, fontWeight: "bold", color: "#333" }}>
        Violations Timeline
      </Typography>

      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          position: "relative",
          paddingLeft: 3,
          borderLeft: "2px solid #ccc", // Cleaner line
        }}
      >
        {combinedTimeline.map(({ type, title, time }, index) => (
          <motion.div
            key={index}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: index * 0.1 }}
          >
            <Card
              sx={{
                position: "relative",
                mb: 2, // More spacing for clarity
                padding: 1.5,
                boxShadow: 1,
                borderRadius: 2,
                maxWidth: "80%",
                bgcolor: "#fff", // Clean background
                border: "1px solid #ddd", // Subtle border
                transition: "all 0.3s ease-in-out",
                "&:hover": {
                  boxShadow: 3,
                  transform: "scale(1.02)", // Slight pop effect
                },
              }}
            >
              {/* Timeline Circle */}
              <CircleIcon
                sx={{
                  color: {
                    violation: "#1E88E5",
                    comment: "#43A047",
                    event: "#8E24AA",
                    email: "#FB8C00",
                  }[type] || "#757575",
                  fontSize: 14,
                  position: "absolute",
                  left: "-25px",
                  top: "50%",
                  transform: "translateY(-50%)",
                }}
              />

              {/* Content */}
              <CardContent sx={{ padding: 1 }}>
                <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
                  {type === "violation" && <ReportIcon sx={{ color: "#1E88E5", mr: 1, fontSize: 20 }} />}
                  {type === "comment" && <ChatBubbleIcon sx={{ color: "#43A047", mr: 1, fontSize: 20 }} />}
                  {type === "event" && <EventNoteIcon sx={{ color: "#8E24AA", mr: 1, fontSize: 20 }} />}
                  {type === "email" && <EmailIcon sx={{ color: "#FB8C00", mr: 1, fontSize: 20 }} />}
                  <Typography variant="body2" sx={{ fontWeight: "bold", color: "#333" }}>
                    {title}
                  </Typography>
                </Box>

                <Typography variant="caption" sx={{ display: "flex", alignItems: "center", color: "#555" }}>
                  <AccessTimeIcon sx={{ fontSize: 12, mr: 0.5 }} />
                  {moment(time).format("YYYY-MM-DD HH:mm:ss")}
                </Typography>
              </CardContent>
            </Card>
          </motion.div>
        ))}
      </Box>

      {/* ✅ Improved Legend */}
      <Legend />
    </Box>
  );
};

// **Legend Component**
const Legend = () => (
  <Box sx={{ mt: 3 }}>
    
    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2 }}>
      {[
        { color: "#1E88E5", icon: <ReportIcon sx={{ fontSize: 14, mr: 1, color: "#1E88E5" }} />, label: "Violation Created/Updated" },
        { color: "#43A047", icon: <ChatBubbleIcon sx={{ fontSize: 14, mr: 1, color: "#43A047" }} />, label: "Comments" },
        { color: "#8E24AA", icon: <EventNoteIcon sx={{ fontSize: 14, mr: 1, color: "#8E24AA" }} />, label: "Events" },
        { color: "#FB8C00", icon: <EmailIcon sx={{ fontSize: 14, mr: 1, color: "#FB8C00" }} />, label: "Email Logs" },
      ].map(({ color, icon, label }, index) => (
        <Box key={index} sx={{ display: "flex", alignItems: "center", mr: 3 }}>
          {icon}
          <Typography variant="body2" sx={{ color }}>
            {label}
          </Typography>
        </Box>
      ))}
    </Box>
  </Box>
);

export default ViolationsTimeline;
